import React, { Component } from "react";
import "./Search.scss";
import Strings from "../../config/Strings";
import {TigerSpinner} from "../../global/tiger-spinner/TigerSpinner";
import SearchResultItem from "./SearchResultItem";
import SeriesResultItem from "./SeriesResultItem";
import {
    ADVANCED_SEARCH_LANGUAGES,
    ADVANCED_SEARCH_TOPICS,
    getPreferredLanguages,
    GetFilteredProducts,
    GetStartpage
} from "./SearchService";
import {withToast} from "../../util/ToastService";
import {Collapse, Form} from "react-bootstrap";
import {PaginationHelp} from "../../global/pagination-help/PaginationHelp";
import {FaChevronDown, FaChevronUp} from "react-icons/fa";
import {Helmet} from "react-helmet";
import {GetProfileRecommendations, GetProfilesOfAccount} from "../profiles/ProfileService";
import {AgeQuickFilter, getAgeFilterOfProfile, getDefaultProfile, storeSelectedProfile} from "./AgeQuickFilter";
import {SearchInput} from "../../global/search-input/SearchInput";

const DEFAULT_SEARCH_CLASSIFICATION = ['RADIOPLAY', 'AUDIOBOOK', 'MUSIC', 'PODCAST', 'PLAYLIST'];

//the length of a product is given in seconds
const DURATION_SELECTIONS = [
    {key: 'ALL', durationMin: "", durationMax: "", labelKey: 'SEARCH_FILTER_ALL'},
    {key: 'SHORT', durationMin: "", durationMax: 899, labelKey: 'SEARCH_DURATION_SHORT'},
    {key: 'MEDIUM', durationMin: 900, durationMax: 1800, labelKey: 'SEARCH_DURATION_MEDIUM'},
    {key: 'LONG', durationMin: 1801, durationMax: "", labelKey: 'SEARCH_DURATION_LONG'},
];

//sortBy and sortDirection are sent as "sortDirection=<sortBy>,<sortDirection>", see createFilterQuery
const SORT_OPTIONS = [
    {key: 'RELEVANCE', sortBy: "", sortDirection: "", labelKey: 'SEARCH_SORT_RELEVANCE'},
    {key: 'PUBLICATION_DATE', sortBy: "publicationDate", sortDirection: "desc", labelKey: 'SEARCH_SORT_PUBLICATION_DATE'},
    {key: 'SHORTEST', sortBy: "attributes.length", sortDirection: "asc", labelKey: 'SEARCH_SORT_SHORTEST'},
    {key: 'LONGEST', sortBy: "attributes.length", sortDirection: "desc", labelKey: 'SEARCH_SORT_LONGEST'},
    {key: 'YOUNGEST_AGE', sortBy: "ageMin", sortDirection: "asc", labelKey: 'SEARCH_SORT_YOUNGEST_AGE'},
    {key: 'HIGHEST_AGE', sortBy: "ageMin", sortDirection: "desc", labelKey: 'SEARCH_SORT_HIGHEST_AGE'},
    {key: 'ORDER_IN_SERIES', sortBy: "order-in-series", sortDirection: "", labelKey: 'SEARCH_SORT_ORDER_IN_SERIES'},
];

const ADVANCED_SEARCH_CLASSIFICATIONS = [
    {value: 'RADIOPLAY', labelKey: 'CLASSIFICATION_RADIOPLAY'},
    {value: 'AUDIOBOOK', labelKey: 'CLASSIFICATION_AUDIOBOOK'},
    {value: 'MUSIC', labelKey: 'CLASSIFICATION_MUSIC'},
    {value: 'PODCAST', labelKey: 'CLASSIFICATION_PODCAST'},
    {value: 'PLAYLIST', labelKey: 'CLASSIFICATION_PLAYLIST'},
];

//the backend and older links use the keyword ALL for "all languages"
const ALL_LANGUAGES = "ALL";

function sameValues(first, second) {
    if (!first || !second || first.length !== second.length) {
        return false;
    }
    const sortedFirst = [...first].sort();
    const sortedSecond = [...second].sort();
    return sortedFirst.every((value, i) => value === sortedSecond[i]);
}

function defaultAdvancedFilter() {
    return {
        classification: [...DEFAULT_SEARCH_CLASSIFICATION],
        //all languages, the ones of the browser are ranked higher (see GetFilteredProducts)
        language: [],
        durationMin: "",
        durationMax: "",
        topics: [],
    };
}

function parseLanguages(languageParam) {
    if (languageParam == null || languageParam === ALL_LANGUAGES) {
        return [];
    }
    return languageParam.split(",").filter(language => language);
}

function getDurationSelection(filter) {
    return DURATION_SELECTIONS.find(selection => String(selection.durationMin) === String(filter.durationMin ?? "")
            && String(selection.durationMax) === String(filter.durationMax ?? ""));
}

//without query there is no relevance, the backend then sorts by publication date (newest first)
function getSortOption(filter, withoutQuery) {
    const option = SORT_OPTIONS.find(option => option.sortBy === (filter.sortBy || "")
            && option.sortDirection === (filter.sortDirection || "")) || SORT_OPTIONS[0];
    return withoutQuery && option.key === 'RELEVANCE' ? SORT_OPTIONS.find(o => o.key === 'PUBLICATION_DATE') : option;
}

class SearchResult extends Component {
    constructor(props) {
        super(props);
        const queryParams = new URLSearchParams(window.location.search);
        //an age given by the URL wins over the age of the selected profile
        this.ageGivenByUrl = queryParams.has("ageMin") || queryParams.has("ageMax");
        //a search is kept in the URL with the query parameter, even if the query is empty
        this.searchGivenByUrl = queryParams.has("query");
        //position of the heading "Suchergebnisse" in the viewport when the sort order was changed
        this.sortHeaderOffset = null;
        this.searchResultHeaderRef = React.createRef();
        this.state = {
            profileId: null,
            loading: false,
            showAdvancedSearch: false,
            filter: props.state != null ? props.state.filter : {
                page: 0,
                totalPages: 0,
                query: queryParams.get("query") ? queryParams.get("query") : "",
                states: ['ACTIVE'],
                language: parseLanguages(queryParams.get("language")),
                ageMin: queryParams.get("ageMin") ? queryParams.get("ageMin") : "",
                ageMax: queryParams.get("ageMax") ? queryParams.get("ageMax") : "",
                durationMin: queryParams.get("durationMin") ? queryParams.get("durationMin") : "",
                durationMax: queryParams.get("durationMax") ? queryParams.get("durationMax") : "",
                topics: queryParams.get("topics")
                        ? queryParams.get("topics").split(",").filter(topic => ADVANCED_SEARCH_TOPICS.some(t => t.key === topic))
                        : [],
                classification: queryParams.get("classification")
                        ? queryParams.get("classification").split(",")
                        : [...DEFAULT_SEARCH_CLASSIFICATION],
                productType: ['AUDIOBOOK'],
//                visibilityCountry: "DE,AT,CH",
                productGroupId: [],
                searchRange: ['ALL'],
                per_page: 20,
                sortBy: queryParams.get("sortBy") ? queryParams.get("sortBy") : "",
                sortDirection: queryParams.get("sortDirection") ? queryParams.get("sortDirection") : "",
            },
            searchedQuery: "",
            searchPerformed: false,
            products: [],
            totalProducts: 0,
            series: [],
            profiles: [],
            selectedProfile: null,
            recommendations: [],
            startPageLoaded: false,
            top10: [],
            top10music: [],
            randomSeries: [],
        };
    }

    async componentDidUpdate(prevProps) {
        if (prevProps.accountId !== this.props.accountId) {
            this.setState({loading: true});
            await this.loadProfiles(this.props.accountId);
            if (this.state.searchPerformed || this.searchGivenByUrl) {
                await this.searchProducts(0);
            } else {
                await this.getStartPage();
            }
        }
    }

    async componentDidMount() {
        document.title = "Suche :: tiger.media";
        this.setState({loading: true});
        await this.loadProfiles(this.props.accountId);
        if (this.searchGivenByUrl) {
            await this.searchProducts(0);
        } else {
            await this.getStartPage();
        }
    }

    async getStartPage() {
        this.setState({
            loading: true,
        });

        await this.loadStartPageContent();

        this.setState({
            loading: false,
        }, () => this.scrollTo());
    }

    //loads the content shown on the start page and below an empty search result
    async loadStartPageContent() {
        //without selected languages the start page shows the content of the preferred language;
        //the start page supports only a single language
        const languages = this.state.filter.language && this.state.filter.language.length > 0
                ? this.state.filter.language : getPreferredLanguages();
        const response = await GetStartpage(languages.length === 1 ? languages[0] : null);
        let top10 = [];
        let top10music = [];
        let randomSeries = [];
        if (response.error === true) {
            this.setState(prevState => ({...prevState, error: response}));
        } else {
            top10 = response.result._embedded.top10;
            top10music = response.result._embedded.top10music;
            randomSeries = response.result._embedded.series;
        }

        this.setState(prevState => ({
            ...prevState,
            //after an error the content is requested again with the next empty search result
            startPageLoaded: response.error !== true,
            top10: top10,
            top10music: top10music,
            randomSeries: randomSeries,
        }));

        await this.loadRecommendations();
    }

    async searchProducts(page) {
        this.setState({
            loading: true,
        });

        let filter = this.state.filter;
        filter.page = page;

        const searchResult = await GetFilteredProducts(filter);
        let filteredProducts = [];
        let filteredSeries = [];
        let totalPages = 0;
        let totalProducts = 0;
        if (searchResult.error === true) {
            this.setState(prevState => ({...prevState, error: searchResult}));
        } else {
            filteredProducts = searchResult.result._embedded.products._embedded?.page;
            totalPages = searchResult.result._embedded.products.page.totalPages;
            totalProducts = searchResult.result._embedded.products.page.totalElements;
            page = searchResult.result._embedded.products.page.number;
            filteredSeries = searchResult.result._embedded.series;
        }

        this.setState(prevState => ({
            ...prevState,
            products: filteredProducts,
            totalProducts: totalProducts,
            series: filteredSeries,
            searchedQuery: filter.query,
            searchPerformed: true,
            filter: {...prevState.filter,
                        page: page,
                        totalPages: totalPages}
        }));

        //an empty search result shows the content of the start page below the "no result" message
        const emptyResult = (!filteredProducts || filteredProducts.length === 0) && (!filteredSeries || filteredSeries.length === 0);
        if (emptyResult && !this.state.startPageLoaded) {
            await this.loadStartPageContent();
        }

        this.setState({
            loading: false,
        }, () => {
            if (this.sortHeaderOffset != null) {
                this.restoreSortHeaderPosition();
            } else {
                this.scrollTo();
            }
        });
    }

    //after a new sort order the heading "Suchergebnisse" is scrolled to the position where the sort order was changed
    restoreSortHeaderPosition() {
        const offset = this.sortHeaderOffset;
        this.sortHeaderOffset = null;
        const header = this.searchResultHeaderRef.current;
        if (header) {
            window.scrollTo(0, window.scrollY + header.getBoundingClientRect().top - offset);
        } else {
            window.scrollTo(0, 0);
        }
        this.updateUrl();
    }

    async loadProfiles(accountId) {
        if (!accountId) {
            this.setState({profiles: [], selectedProfile: null, recommendations: []});
            return;
        }
        const profilesResponse = await GetProfilesOfAccount(accountId);
        if (profilesResponse.error) {
            return;
        }
        const profiles = profilesResponse.result._embedded?.profiles || [];
        if (profiles.length === 0) {
            this.setState({profiles: [], selectedProfile: null});
            return;
        }
        const defaultProfile = getDefaultProfile(profiles);
        this.setState(prevState => ({
            ...prevState,
            profiles: profiles,
            selectedProfile: defaultProfile.id,
            filter: this.ageGivenByUrl ? prevState.filter : this.filterWithAgeOfProfile(prevState.filter, defaultProfile),
        }));
    }

    filterWithAgeOfProfile(filter, profile) {
        return {...filter, ...getAgeFilterOfProfile(profile)};
    }

    getSelectedProfile() {
        return this.state.profiles.find(profile => Number(profile.id) === Number(this.state.selectedProfile));
    }

    async loadRecommendations() {
        const profile = this.getSelectedProfile();
        if (!profile) {
            this.setState({recommendations: []});
            return;
        }
        const response = await GetProfileRecommendations(profile.id, profile.ageRange?.min, profile.ageRange?.max, null);
        if (!response.error === true && response.result?._embedded?.simpleHalRepresentationModels) {
            this.setState(prevState => ({...prevState,
                recommendations: response.result._embedded.simpleHalRepresentationModels}
            ));
        }
    }

    scrollTo() {
        window.scrollTo(0, 0)
        this.updateUrl();
    }

    //reflects the search term and every filter parameter that deviates from its
    //default value in the URL, so searches stay shareable/bookmarkable and survive a reload
    updateUrl() {
        const filter = this.state.filter;
        const params = new URLSearchParams();
        if (filter.query || this.state.searchPerformed) {
            params.set("query", filter.query || "");
        }
        if (filter.language.length > 0) {
            params.set("language", filter.language.join(","));
        }
        //the age is always kept as soon as one is selected, so a profile based preselection survives a reload
        if (filter.ageMin !== "" && filter.ageMin != null) {
            params.set("ageMin", filter.ageMin);
        }
        if (filter.ageMax !== "" && filter.ageMax != null) {
            params.set("ageMax", filter.ageMax);
        }
        if (filter.durationMin !== "" && filter.durationMin != null) {
            params.set("durationMin", filter.durationMin);
        }
        if (filter.durationMax !== "" && filter.durationMax != null) {
            params.set("durationMax", filter.durationMax);
        }
        if (filter.topics && filter.topics.length > 0) {
            params.set("topics", filter.topics.join(","));
        }
        if (!sameValues(filter.classification, DEFAULT_SEARCH_CLASSIFICATION)) {
            params.set("classification", filter.classification.join(","));
        }
        if (filter.sortBy) {
            params.set("sortBy", filter.sortBy);
            if (filter.sortDirection) {
                params.set("sortDirection", filter.sortDirection);
            }
        }
        const queryString = params.toString();
        if (queryString) {
            window.history.pushState({}, null, "/search/?" + queryString);
        } else if (window.location.search) {
            //e.g. back on the start page after a reset, the parameters of the former search are removed
            window.history.pushState({}, null, "/search/");
        }
    }

    //starts a new search with the changed filters
    applyFilter(filterChange) {
        this.setState(prevState => ({...prevState, filter: {...prevState.filter, ...filterChange}}),
                () => this.searchProducts(0));
    }

    handlePageClick = (event) => {
        console.log("User requested page number " + event.selected);
        window.scrollTo(0, 0)
        this.setState(prevState => ({...prevState, page: event.selected}), () => this.searchProducts(event.selected));
    };

    handleSelectProfile = (selectedProfile) => {
        const profile = this.state.profiles.find(p => Number(p.id) === Number(selectedProfile));
        if (!profile) {
            return;
        }
        storeSelectedProfile(profile.id);
        this.setState(prevState => ({
            ...prevState,
            selectedProfile: profile.id,
            filter: this.filterWithAgeOfProfile(prevState.filter, profile),
        }), () => {
            this.loadRecommendations();
            if (this.state.searchPerformed) {
                this.searchProducts(0);
            }
        });
    };

    applyAgeFilter = (ageFilter) => {
        this.applyFilter(ageFilter);
    };

    handleSortChange = (e) => {
        const option = SORT_OPTIONS.find(o => o.key === e.target.value) || SORT_OPTIONS[0];
        const header = this.searchResultHeaderRef.current;
        this.sortHeaderOffset = header ? header.getBoundingClientRect().top : null;
        this.setState(prevState => ({...prevState, filter: {...prevState.filter, sortBy: option.sortBy, sortDirection: option.sortDirection}}),
                () => this.searchProducts(0));
    };

    toggleAdvancedSearch = () => {
        this.setState(prevState => {
            const showAdvancedSearch = !prevState.showAdvancedSearch;
            return {
                ...prevState,
                showAdvancedSearch,
                //collapsing behaves like pressing "reset": the advanced parameters
                //go back to their default values
                filter: showAdvancedSearch ? prevState.filter : {...prevState.filter, ...defaultAdvancedFilter()},
            };
        }, () => {
            if (!this.state.showAdvancedSearch && this.state.searchPerformed) {
                this.searchProducts(0);
            }
        });
    };

    //without query the reset shows the start page again instead of searching with the default filters
    resetAdvancedSearch = () => {
        if (this.state.filter.query && this.state.filter.query.trim()) {
            this.applyFilter(defaultAdvancedFilter());
            return;
        }
        this.setState(prevState => ({
            ...prevState,
            searchPerformed: false,
            searchedQuery: "",
            products: [],
            totalProducts: 0,
            series: [],
            filter: {...prevState.filter, ...defaultAdvancedFilter(), query: "", page: 0, totalPages: 0},
        }), () => this.getStartPage());
    };

    //"all" deselects every single value, selecting a single value deselects "all"
    toggleMultiSelection = (key, value) => {
        const current = this.state.filter[key] || [];
        const updated = current.includes(value)
                ? current.filter(c => c !== value)
                : [...current, value];
        this.applyFilter({[key]: updated});
    };

    selectAllClassifications = () => {
        this.applyFilter({classification: [...DEFAULT_SEARCH_CLASSIFICATION]});
    };

    selectAllTopics = () => {
        this.applyFilter({topics: []});
    };

    selectAllLanguages = () => {
        this.applyFilter({language: []});
    };

    handleDurationSelection = (selection) => {
        this.applyFilter({durationMin: selection.durationMin, durationMax: selection.durationMax});
    };

    renderChip(key, label, selected, onClick) {
        return (
            <button type="button" key={key} className={"search-filter-chip" + (selected ? " selected" : "")}
                    aria-pressed={selected} onClick={onClick}>
                {label}
            </button>
        );
    }

    renderSearchForm() {
        return (
            <SearchInput query={this.state.filter.query}
                         showDescription={true}
                         languages={this.state.filter.language}
                         ageMin={this.state.filter.ageMin}
                         ageMax={this.state.filter.ageMax}
                         onQueryChange={(query) => this.setState(prevState => ({...prevState, filter: {...prevState.filter, query: query}}))}
                         onSearch={() => this.searchProducts(0)}/>
        );
    }

    renderResultSummary() {
        const series = this.state.series || [];
        const seriesText = series.length === 1
                ? Strings.SEARCH_RESULT_SUMMARY_SERIES_SINGULAR
                : Strings.SEARCH_RESULT_SUMMARY_SERIES_PLURAL.replace("{0}", series.length);
        return (
            <div className="search-result-summary">
                <span className="search-result-summary-query">
                    {this.state.searchedQuery
                            ? Strings.SEARCH_RESULT_SUMMARY.replace("{0}", this.state.searchedQuery)
                            : Strings.SEARCH_RESULT_SUMMARY_WITHOUT_QUERY}
                </span>
                <span className="search-result-summary-count">
                    {" · " + seriesText + ", " + Strings.SEARCH_RESULT_SUMMARY_TITLES.replace("{0}", this.state.totalProducts || 0)}
                </span>
            </div>
        );
    }

    renderQuickFilters() {
        return (
            <AgeQuickFilter profiles={this.state.profiles}
                            selectedProfile={this.state.selectedProfile}
                            showProfiles={!!this.props.accountId}
                            ageMin={this.state.filter.ageMin}
                            ageMax={this.state.filter.ageMax}
                            onSelectProfile={this.handleSelectProfile}
                            onAgeFilterChange={this.applyAgeFilter}/>
        );
    }

    renderAdvancedSearchPanel() {
        const filter = this.state.filter;
        const classification = filter.classification || [];
        const allClassifications = classification.length === 0 || sameValues(classification, DEFAULT_SEARCH_CLASSIFICATION);
        const languages = filter.language || [];
        const selectedDuration = getDurationSelection(filter);
        const topics = filter.topics || [];

        return (
            <>
                <button type="button" id={"advanced-search-toggle-button"} className={"advanced-search-toggle-button"}
                        aria-expanded={this.state.showAdvancedSearch}
                        onClick={this.toggleAdvancedSearch}>
                    {Strings.ADVANCED_SEARCH_TOGGLE_TEXT} {this.state.showAdvancedSearch ? <FaChevronUp/> : <FaChevronDown/>}
                </button>
                <Collapse in={this.state.showAdvancedSearch}>
                    <div className={"advanced-search-container"}>
                        <div className={"advanced-search-group"}>
                            <div className={"advanced-search-label"}>{Strings.ADVANCED_SEARCH_TYPE}</div>
                            <div className="search-filter-chips">
                                {this.renderChip('ALL', Strings.SEARCH_FILTER_ALL, allClassifications, this.selectAllClassifications)}
                                {ADVANCED_SEARCH_CLASSIFICATIONS.map(c => this.renderChip(c.value, Strings[c.labelKey],
                                        !allClassifications && classification.includes(c.value),
                                        () => allClassifications
                                                ? this.applyFilter({classification: [c.value]})
                                                : this.toggleMultiSelection('classification', c.value)))}
                            </div>
                        </div>
                        <div className={"advanced-search-group"}>
                            <div className={"advanced-search-label"}>{Strings.ADVANCED_SEARCH_DURATION}</div>
                            <div className="search-filter-chips">
                                {DURATION_SELECTIONS.map(d => this.renderChip(d.key, Strings[d.labelKey],
                                        selectedDuration?.key === d.key, () => this.handleDurationSelection(d)))}
                            </div>
                        </div>
                        <div className={"advanced-search-group"}>
                            <div className={"advanced-search-label"}>{Strings.ADVANCED_SEARCH_TOPIC}</div>
                            <div className="search-filter-chips">
                                {this.renderChip('ALL', Strings.SEARCH_FILTER_ALL, topics.length === 0, this.selectAllTopics)}
                                {ADVANCED_SEARCH_TOPICS.map(t => this.renderChip(t.key, Strings[t.labelKey],
                                        topics.includes(t.key), () => this.toggleMultiSelection('topics', t.key)))}
                            </div>
                        </div>
                        <div className={"advanced-search-group"}>
                            <div className={"advanced-search-label"}>{Strings.ADVANCED_SEARCH_LANGUAGE}</div>
                            <div className="search-filter-chips">
                                {this.renderChip('ALL', Strings.LANGUAGE_ALL, languages.length === 0, this.selectAllLanguages)}
                                {ADVANCED_SEARCH_LANGUAGES.map(l => this.renderChip(l.value, Strings[l.labelKey],
                                        languages.includes(l.value), () => this.toggleMultiSelection('language', l.value)))}
                            </div>
                        </div>
                        <div className={"advanced-search-buttons"}>
                            <button type="button" id={"advanced-search-reset-button"} className={"advanced-search-reset-button"}
                                    onClick={this.resetAdvancedSearch}>
                                {Strings.ADVANCED_SEARCH_RESET}
                            </button>
                        </div>
                    </div>
                </Collapse>
            </>
        );
    }

    renderSortSelect() {
        const withoutQuery = !this.state.searchedQuery || !this.state.searchedQuery.trim();
        return (
            <div className="search-result-sort-container">
                <label htmlFor="search-result-sort-select">{Strings.SEARCH_SORT_LABEL}</label>
                <Form.Select id="search-result-sort-select" className="search-result-sort-select"
                             value={getSortOption(this.state.filter, withoutQuery).key}
                             onChange={this.handleSortChange}>
                    {SORT_OPTIONS.map(option => (
                        <option value={option.key} key={option.key}
                                disabled={withoutQuery && option.key === 'RELEVANCE'}>{Strings[option.labelKey]}</option>
                    ))}
                </Form.Select>
            </div>
        );
    }

    renderSearchResults(products, series) {
        return (
            <>
                {series && series.length > 0 &&
                <div className="series-container">
                    <div>
                        <div className="search-title">
                            {Strings.SEARCH_RESULT_SERIES} (<a className="all-heros-headline-link" href={"/series/all"}>{Strings.SEARCH_RESULT_SERIES_LINK_TEXT}</a>)
                        </div>
                        <hr className={"search-hr"}/>
                    </div>
                    <div className="series-items-container-wrapper">
                        <div className="series-items-container">
                            {series.map(s => (
                                    <div className="series-item" key={s.id}>
                                        <SeriesResultItem series={s} showLanguage={true}/>
                                    </div>
                            ))}
                        </div>
                    </div>
                </div>}

                {products && products.length > 0 &&
                <>
                <div className="search-container">
                    <div className="search-result-header" ref={this.searchResultHeaderRef}>
                        <div className="search-title">
                            {Strings.SEARCH_RESULT_TITLE}
                        </div>
                        {this.renderSortSelect()}
                        <hr className={"search-hr"}/>
                    </div>
                    <div className="search-items-container">
                        {products.map(p => (
                            <div className="search-item" key={p.id}>
                                <SearchResultItem product={p} showLanguage={true}/>
                            </div>
                        ))}
                    </div>
                </div>
                    <PaginationHelp
                        page={this.state.filter.page}
                        totalPages={this.state.filter.totalPages}
                        handlePageClick={this.handlePageClick}
                    />
                </>}
            </>
        );
    }

    renderStartPageContent() {
        const randomSeries = this.state.randomSeries;
        const recommendations = this.state.recommendations;
        const top10 = this.state.top10;
        const top10music = this.state.top10music;
        const selectedProfile = this.getSelectedProfile();

        return (
            <>
                {randomSeries && randomSeries.length > 0  &&
                        <div className="series-container">
                            <div>
                                <div className="search-title">
                                    Serien aus unserem Programm (<a className="all-heros-headline-link" href={"/series/all"}>alle</a>)
                                </div>
                                <hr className={"search-hr"}/>
                            </div>
                            <div className="series-items-container-wrapper">
                                <div className="series-items-container">
                                    {randomSeries.map(s => (
                                            <div className="series-item" key={s.id}>
                                                <SeriesResultItem series={s} showLanguage={true}/>
                                            </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                }
                {this.props.accountId && recommendations && recommendations.length > 0 &&
                    <div className="search-container">
                        <div>
                            <div className="search-title">
                                {Strings.SEARCH_RECOMMENDATION_TITLE + (selectedProfile ? selectedProfile.name : "")}
                            </div>
                            <hr className={"search-hr"}/>
                        </div>
                        <div className="search-items-container">
                            {recommendations.map(p => (
                                <div className="search-item" key={p.id}>
                                    <SearchResultItem product={p} showLanguage={true}/>
                                </div>
                            ))}
                        </div>
                    </div>
                }
                {top10 && top10.length > 0 &&
                    <div className="search-container">
                        <div>
                            <div className="search-title">
                                Top 10 Hörspiele/Hörbücher
                            </div>
                            <hr className={"search-hr"}/>
                        </div>
                        <div className="search-items-container">
                            {top10.map(p => (
                                <div className="search-item" key={p.id}>
                                    <SearchResultItem product={p} showLanguage={true}/>
                                </div>
                            ))}
                        </div>
                        </div>
                }
                {top10music && top10music.length > 0 &&
                        <div className="search-container">
                            <div>
                                <div className="search-title">
                                    Top 10 Musik
                                </div>
                                <hr className={"search-hr"}/>
                            </div>
                            <div className="search-items-container">
                                {top10music.map(p => (
                                        <div className="search-item" key={p.id}>
                                            <SearchResultItem product={p} showLanguage={true}/>
                                        </div>
                                ))}
                            </div>
                        </div>
                }
            </>
        );
    }

    render() {
        const products = this.state.products;
        const series = this.state.series;

        if(this.state.loading) {
            return (
                <div className="search-loading" style={{width: "100%", height: "500px", textAlign: "center", fontSize: "20px"}}>
                    <br />
                    <br />
                    <br />
                    <TigerSpinner />
                    <br />
                    {Strings.PRODUCT_DETAILS_REFRESH} <span className="refresh-link" onClick={() => {
                        window.location.reload(false);
                }}>{Strings.PRODUCT_DETAILS_REFRESH_LINK}</span>
                </div>
            )
        }

        const hasResults = (products && products.length > 0) || (series && series.length > 0);

        return (
            <div className={"search-result"}>
                <Helmet>
                    <meta charSet="utf-8"/>
                    <title>{Strings.SEARCH_PAGE_TITLE}</title>
                    <meta name="description" content={Strings.SEARCH_PAGE_DESCRIPTION}/>
                    <meta name="keywords" content={Strings.SEARCH_PAGE_KEYWORDS}/>
                </Helmet>

                {this.renderSearchForm()}
                {hasResults && this.renderResultSummary()}
                {this.renderQuickFilters()}
                {this.renderAdvancedSearchPanel()}

                {hasResults ?
                    this.renderSearchResults(products, series)
                    :
                    <>
                        {this.state.searchPerformed &&
                            <div className="search-container">
                                <div className={"no-result-container"}>{Strings.SEARCH_NO_RESULT}</div>
                            </div>}
                        {this.renderStartPageContent()}
                    </>
                }
            </div>
        );
    }
}

export default withToast(SearchResult);
